import { ragaTargetCents } from '@swarasense/shared';
import type { RagaRecord, Swarasthana } from '@swarasense/shared';
import type { TonePlayer } from '../audio/tone.js';
import { centsToFreq } from '../audio/tone.js';
import { swaraLabel } from './notation-lane.js';

/** Slug for the raga's full content page (`/ragas/<slug>/`) — mirrors
 * `scripts/gen-content/util.ts`'s `slugify` (kept in sync by hand: that module
 * is a Node build script, a separate build graph, same convention as
 * `skeleton.ts`'s `ActiveNav`). Connects the in-app rail to the full raga
 * page so the two are one companion (Connected-Swarasense W10). */
function railSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}


/**
 * UI reimagine, Task 3: the raga companion side-rail (desktop) / "About this
 * raga" bottom-sheet (mobile) — the centrepiece of the practice-page redesign.
 * It sits beside the pitch stage (never a separate page to navigate to) and
 * makes the arohana/avarohana **playable**: tap a swara chip to hear it over
 * the drone, or "Play scale ▶" a whole row as a phrase. See
 * `docs/superpowers/specs/2026-08-04-ui-reimagine-kalamkari-design.md`'s
 * "Practice page" section and this plan's `task-3-brief.md`.
 *
 * The module is split in two independently-useful halves:
 * - `playableScale` (below): a PURE function — raga record + tonic Hz in,
 *   playable notes out. No DOM, no audio side effects — trivially unit
 *   tested (`raga-rail.test.ts`), same shape as `ribbon-geometry.ts`'s
 *   pure-geometry/`ribbon.ts`-DOM split.
 * - `renderRagaRail` (further down): the DOM renderer + interaction wiring,
 *   built on top of it.
 */

/** One playable note in an arohana/avarohana row.
 * - `swara`: the DISPLAY label the child sees — a plain 7-symbol swara letter
 *   (S R G M P D N, lower/upper-case marks the variant, e.g. Mohanam's
 *   Chatushruti Rishabha is still just "R") with an octave-up combining mark
 *   (`swaraLabel(sym, 1)`, the same convention `notation-lane.ts` uses for
 *   the ribbon's own "Ṡ") on the note that closes an ascending row / opens a
 *   descending one. Never the internal 16-swarasthana code (e.g. "R2"/"G3")
 *   `RagaRecord.arohana`/`avarohana` actually store — those carry a digit,
 *   which would break the rail's NUMBER-FREE constraint for a child reader.
 * - `cents`: cents above the (madhya sthayi) tonic Sa, octave-wrapped
 *   (+1200) for that same closing/opening note — never shown to the child;
 *   it only exists to derive `freqHz` below.
 * - `freqHz`: `centsToFreq(saHz, cents)` — what the tone player actually
 *   sounds when this chip is tapped.
 * - `oscillatory`: true when this raga's `gamaka_profile` marks the swara as
 *   anything other than `'steady'` — drives the rail's "∿" chip mark. Always
 *   `false` for a lean (code-derived) raga, whose default profile is
 *   `steady` for every swara (no gamaka claims — see `isEnrichedRaga` below).
 */
export interface PlayableNote {
  swara: string;
  cents: number;
  freqHz: number;
  oscillatory: boolean;
}

export interface PlayableScale {
  arohana: PlayableNote[];
  avarohana: PlayableNote[];
}

/**
 * The display symbol (a `swara_variants` key, e.g. "R"/"g") whose value is
 * this `Swarasthana` (e.g. "R2") — the inverse of `swara_variants`, which
 * `ragaTargetCents`/`gamaka_profile` are both keyed by, while
 * `arohana`/`avarohana` store the resolved `Swarasthana` itself (see
 * `derive.ts`). Falls back to the swarasthana's own code if somehow absent
 * (defensive only — `validateRagaRecord` guarantees every non-"S"
 * arohana/avarohana entry has a `swara_variants` entry, and "S" is always
 * present as `swara_variants.S === 'S'` for every derived record).
 */
function displaySymbolFor(record: RagaRecord, swarasthana: Swarasthana): string {
  for (const [symbol, value] of Object.entries(record.swara_variants)) {
    if (value === swarasthana) return symbol;
  }
  return swarasthana;
}

/**
 * One row (arohana or avarohana) of playable notes. `tonicIndex` is the
 * position in `row` that is the raga's OWN tonic Sa (index 0 for an
 * ascending arohana, the last index for a descending avarohana — both
 * derivations in `derive.ts` always place it there). Every OTHER "S" in the
 * row is the octave-up Ṡ that closes the ascent / opens the descent, and
 * gets +1200 cents.
 */
function buildRow(
  record: RagaRecord,
  saHz: number,
  row: readonly Swarasthana[],
  tonicIndex: number,
): PlayableNote[] {
  const targetCents = ragaTargetCents(record);
  const gamakaProfile = record.gamaka_profile ?? {};
  return row.map((swarasthana, i) => {
    const symbol = displaySymbolFor(record, swarasthana);
    const baseCents = targetCents[symbol] ?? 0;
    const isOctaveTop = swarasthana === 'S' && i !== tonicIndex;
    const cents = isOctaveTop ? baseCents + 1200 : baseCents;
    const style = gamakaProfile[symbol]?.style ?? 'steady';
    return {
      swara: isOctaveTop ? swaraLabel(symbol, 1) : symbol,
      cents,
      freqHz: centsToFreq(saHz, cents),
      oscillatory: style !== 'steady',
    };
  });
}

/**
 * The raga's playable arohana + avarohana: swara + cents + frequency +
 * "does this swara take a gamaka" for every note, ready for the rail's chips
 * (or any other UI) to render and sound. Pure and deterministic — same
 * `record`/`saHz` always produce the same result, with no audio/DOM touched.
 */
export function playableScale(record: RagaRecord, saHz: number): PlayableScale {
  return {
    arohana: buildRow(record, saHz, record.arohana, 0),
    avarohana: buildRow(record, saHz, record.avarohana, record.avarohana.length - 1),
  };
}

/** "verified"/"single-source" ragas have a hand-curated `gamaka_profile`
 * (with `note`s), `character`, and `common_pitfalls` — the SAME rule
 * `scripts/gen-content/raga-page.ts`'s `ENRICHED_STATUSES` uses, so the rail
 * and the `/ragas/<slug>/` page never disagree about which ragas get the
 * full write-up vs. the honest "not yet curated" note. "code-derived" (and
 * any unexpected "conflict") ragas are lean. */
const ENRICHED_STATUSES = new Set(['verified', 'single-source']);

/** Whether `record` has a real, hand-curated gamaka/character/pitfalls
 * write-up (vs. a mechanically-derived scale with no gamaka claims). */
export function isEnrichedRaga(record: Pick<RagaRecord, 'validation_status'>): boolean {
  return ENRICHED_STATUSES.has(record.validation_status);
}

// --------------------------------------------------------------- DOM -----

/** Own copy of the trust boundary `app.ts`'s `stageCaptionHtml`/
 * `results-panel.ts` already document: raga name/character/pitfalls/gamaka
 * notes all come from our own seed data (never user input) — escaped anyway
 * (cheap, and matches `results-panel.ts`'s own defensive habit) rather than
 * relied upon to be safe. */
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function titleCaseJaati(janyaType: string): string {
  return janyaType
    .split('-')
    .map((word) => (word.length > 0 ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join('-');
}

/** "Melakarta raga #N" / "Janya of Melakarta N" / "Janya raga" (no parent
 * on record), each followed by "· {Jaati} jaati" — the header line the
 * approved mockup shows under the raga name. The melakarta INDEX is a
 * classification/catalogue number (like a raga's other name — "Raga 28"),
 * not a score or measurement, so it stays even though the rail is otherwise
 * number-free (see the module doc + `PlayableNote`'s own note on this). */
function classificationText(record: RagaRecord): string {
  const parent =
    record.type === 'melakarta'
      ? `Melakarta raga #${record.melakarta_number}`
      : record.parent_melakarta !== null
        ? `Janya of Melakarta ${record.parent_melakarta}`
        : 'Janya raga';
  return `${parent} · ${titleCaseJaati(record.janya_type)} jaati`;
}

/** The honest, no-fabricated-gamaka note for a lean (code-derived) raga —
 * same tone as `raga-page.ts`'s `renderLeanBody`, just shorter for the
 * rail's tighter space. */
function leanNoteHtml(): string {
  return `<p class="raga-rail-lean-note">This scale is code-derived — a scale
    reference, not a verified gamaka guide yet. Practise it live to hear how
    it actually moves; we haven't curated gamaka notes for it.</p>`;
}

/** The enriched write-up: per-swara gamaka notes (only swaras with a curated
 * `.note` — a lean raga's default profile has none), `character`, and
 * `common_pitfalls`. Mirrors `raga-page.ts`'s `renderEnrichedBody`, minus
 * the sources list (out of scope for this companion — the full `/ragas/`
 * page is still the place for that). */
function enrichedBodyHtml(record: RagaRecord): string {
  const gamakaProfile = record.gamaka_profile ?? {};
  const gamakaItems = Object.keys(record.swara_variants)
    .map((symbol) => ({ symbol, entry: gamakaProfile[symbol] }))
    .filter(
      (x): x is { symbol: string; entry: NonNullable<typeof x.entry> } =>
        x.entry?.note !== undefined,
    )
    .map(
      ({ symbol, entry }) =>
        `<li><strong>${escapeHtml(symbol)}${entry.style !== 'steady' ? ' ∿' : ''}</strong> — ${escapeHtml(entry.note!)}</li>`,
    )
    .join('');
  const gamakaHtml =
    gamakaItems.length > 0
      ? `<h3 class="raga-rail-subhead">Gamaka</h3><ul class="raga-rail-gamaka-list">${gamakaItems}</ul>`
      : '';

  const characterHtml = record.character
    ? `<h3 class="raga-rail-subhead">Character</h3><p class="raga-rail-character">${escapeHtml(record.character)}</p>`
    : '';

  const pitfallsHtml =
    record.common_pitfalls && record.common_pitfalls.length > 0
      ? `<h3 class="raga-rail-subhead">Common pitfalls</h3><ul class="raga-rail-pitfalls">${record.common_pitfalls
          .map((p) => `<li>${escapeHtml(p)}</li>`)
          .join('')}</ul>`
      : '';

  return `${gamakaHtml}${characterHtml}${pitfallsHtml}`;
}

/** A single note's approximate on-screen "lit while it sounds" duration —
 * mirrors `audio/tone.ts`'s own (private) `DEFAULT_DURATION_MS`. This is a
 * VISUAL nicety only (the optional chip-highlight sweep on "Play scale ▶"),
 * not a timing source the audio itself depends on, so an approximate,
 * independently-maintained constant is fine even though it isn't imported
 * from `tone.ts` directly. */
const CHIP_LIT_MS = 700;

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** Briefly marks `chip` as "sounding" (`.is-playing`) — the tap feedback for
 * a single swara chip. */
function litChip(chip: HTMLElement): void {
  chip.classList.add('is-playing');
  window.setTimeout(() => chip.classList.remove('is-playing'), Math.max(0, CHIP_LIT_MS - 40));
}

/** The optional "Play scale ▶" sweep: steps the highlight across `chips` in
 * time with the phrase, one at a time. Under `prefers-reduced-motion`, that
 * degrades to a single static highlight over the whole row for the phrase's
 * total duration instead of an animated sweep (still visible feedback that
 * something is playing, no motion). */
function sweepChips(chips: readonly HTMLElement[]): void {
  if (reducedMotion()) {
    for (const chip of chips) chip.classList.add('is-playing');
    window.setTimeout(() => {
      for (const chip of chips) chip.classList.remove('is-playing');
    }, chips.length * CHIP_LIT_MS);
    return;
  }
  chips.forEach((chip, i) => {
    window.setTimeout(() => litChip(chip), i * CHIP_LIT_MS);
  });
}

export interface RagaRailDeps {
  /** The CURRENT raga record, read fresh on every `update()` (and at the
   * initial render) — `undefined` before the seed bundle has loaded, or if
   * the store's `ragaName` doesn't resolve to a known raga. */
  getRecord: () => RagaRecord | undefined;
  /** The tonic Sa frequency to play chips/phrases at, read fresh at the
   * moment of each play (not cached) — mirrors `app.ts`'s own `saHz()`. */
  saHz: () => number;
  tonePlayer: TonePlayer;
  /** Starts the tanpura drone if it isn't already running — the same
   * "ensure the tonal reference is there before a tone plays" contract
   * `ear-training.ts` already uses (`app.ts`'s `enterEarMode` passes the
   * identical `if (!tanpura.running) await startTanpura()` lambda). */
  ensureDrone: () => Promise<void>;
}

export interface RagaRail {
  /** Re-renders for whatever `deps.getRecord()` returns NOW — call after the
   * ragam changes (e.g. from `app.ts`'s `store.subscribe`). */
  update(): void;
}


/**
 * Mounts the raga companion rail into `container` (the `<aside>` `app.ts`
 * reserves beside/below the stage) and returns `{ update }` for re-rendering
 * on raga change. Renders: a header (name · classification · jaati) with a
 * combined "Play scale ▶" (the mobile bottom-sheet's ALWAYS-VISIBLE compact
 * summary — see the module doc), then the arohana/avarohana as playable
 * chip rows (each row's own heading IS its "{Arohana|Avarohana} ▶" play
 * trigger), then the gamaka/character/pitfalls write-up (enriched) or the
 * honest lean note — all behind a native `<details>`/`<summary>` "About this
 * raga ▾" disclosure so the SAME markup serves both layouts: forced open on
 * desktop (the side-rail always shows everything), a real collapsible sheet
 * on mobile (`styles.css` hides the `<summary>` toggle at the desktop
 * breakpoint; this function keeps `<details open>` in sync with it).
 */
export function renderRagaRail(container: HTMLElement, deps: RagaRailDeps): RagaRail {
  // The always-visible part of the rail is the name + playable scale (that's the
  // point of having it beside the ribbon); the longer gamaka/character/pitfalls
  // write-up lives in a collapsible `<details>` — CLOSED by default (so the rail
  // stays roughly stage-height, no scrolling) with the reader's open/closed
  // choice remembered across ragas + reloads, on desktop AND mobile alike.
  const RAIL_OPEN_KEY = 'swarasense.ragaRailOpen';
  const readOpenPref = (): boolean => {
    try {
      return localStorage.getItem(RAIL_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  };
  const writeOpenPref = (open: boolean): void => {
    try {
      localStorage.setItem(RAIL_OPEN_KEY, open ? '1' : '0');
    } catch {
      /* private mode / storage disabled — the default (closed) is fine */
    }
  };

  /** Plays `notes` as a phrase over the drone, sweeping `chips` in time with
   * it — the shared body behind every "▶" trigger (a single chip, a row's
   * "{Direction} ▶" heading, and the compact summary's "Play scale ▶"). */
  function playPhrase(notes: readonly PlayableNote[], chips: readonly HTMLElement[]): void {
    // Stop before a new play so a fast re-tap cleanly restarts instead of layering
    // tones (carry-forward from the tone player; mirrors ear-training.ts).
    deps.tonePlayer.stop();
    deps.tonePlayer.unlock(); // SYNC, first thing, still inside the click gesture
    sweepChips(chips);
    void (async () => {
      await deps.ensureDrone();
      await deps.tonePlayer.playPhrase(
        deps.saHz(),
        notes.map((n) => ({ cents: n.cents })),
      );
    })();
  }

  function renderRow(direction: 'arohana' | 'avarohana', notes: PlayableNote[]): string {
    const label = direction === 'arohana' ? 'Arohana' : 'Avarohana';
    const chips = notes
      .map(
        (n, i) =>
          `<button type="button" class="raga-rail-chip" data-idx="${i}" aria-label="${escapeHtml(n.swara)}${n.oscillatory ? ', oscillating' : ''}">${escapeHtml(n.swara)}${n.oscillatory ? '<span aria-hidden="true"> ∿</span>' : ''}</button>`,
      )
      .join('');
    return `<section class="raga-rail-row" data-direction="${direction}">
      <button type="button" class="raga-rail-row-play" data-direction="${direction}">${label} <span aria-hidden="true">▶</span></button>
      <div class="raga-rail-chips" role="group" aria-label="${label}">${chips}</div>
    </section>`;
  }

  function wireRow(
    root: HTMLElement,
    direction: 'arohana' | 'avarohana',
    notes: PlayableNote[],
  ): void {
    const section = root.querySelector<HTMLElement>(
      `.raga-rail-row[data-direction="${direction}"]`,
    );
    if (!section) return;
    const chipEls = Array.from(section.querySelectorAll<HTMLButtonElement>('.raga-rail-chip'));
    chipEls.forEach((chip, i) => {
      const note = notes[i];
      if (!note) return;
      chip.addEventListener('click', () => {
        deps.tonePlayer.stop(); // clean restart on a fast re-tap (no layered tones)
        deps.tonePlayer.unlock(); // SYNC, first thing, pre-await
        litChip(chip);
        void (async () => {
          await deps.ensureDrone();
          await deps.tonePlayer.playSwara(deps.saHz(), note.cents);
        })();
      });
    });
    section
      .querySelector<HTMLButtonElement>('.raga-rail-row-play')
      ?.addEventListener('click', () => playPhrase(notes, chipEls));
  }

  function render(): void {
    const record = deps.getRecord();
    if (!record) {
      container.innerHTML = '<p class="raga-rail-empty">Loading raga…</p>';
      return;
    }
    const scale = playableScale(record, deps.saHz());
    const enriched = isEnrichedRaga(record);

    container.innerHTML = `
      <div class="raga-rail-summary">
        <h2 class="raga-rail-name">${escapeHtml(record.name)}</h2>
        <p class="raga-rail-classify">${escapeHtml(classificationText(record))}</p>
        <button type="button" class="btn raga-rail-play-all">Play scale <span aria-hidden="true">▶</span></button>
        ${renderRow('arohana', scale.arohana)}
        ${renderRow('avarohana', scale.avarohana)}
      </div>
      <details class="raga-rail-details">
        <summary class="raga-rail-toggle">Gamaka &amp; character <span aria-hidden="true">▾</span></summary>
        <div class="raga-rail-notes">
          ${enriched ? enrichedBodyHtml(record) : leanNoteHtml()}
        </div>
      </details>
      <a class="raga-rail-more" href="/ragas/${railSlug(record.name)}/">Full ${escapeHtml(record.name)} page <span aria-hidden="true">→</span></a>`;

    wireRow(container, 'arohana', scale.arohana);
    wireRow(container, 'avarohana', scale.avarohana);

    const allChips = Array.from(container.querySelectorAll<HTMLElement>('.raga-rail-chip'));
    const allNotes = [...scale.arohana, ...scale.avarohana];
    container
      .querySelector<HTMLButtonElement>('.raga-rail-play-all')
      ?.addEventListener('click', () => playPhrase(allNotes, allChips));

    // Restore the remembered open/closed state; persist the reader's toggles.
    const details = container.querySelector<HTMLDetailsElement>('.raga-rail-details');
    if (details) {
      details.open = readOpenPref();
      details.addEventListener('toggle', () => writeOpenPref(details.open));
    }
  }

  render();
  return { update: render };
}
